import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import AuthPage from './pages/auth-page'
import LoginPage from './pages/login-page'
import useAppData from './hooks/use-app-data'
import { FullPageLoading } from './components/loading'

// General pages
import PublicLayout from './layouts/public-layout'
import BlankLayout from './layouts/blank-layout'
import ErrorPage from './pages/error-page'

// Admin pages
const AdminLayout = lazy(() => import('./layouts/admin-layout'))
const DashboardPage = lazy(() => import('./pages/dashboard-page'))

// Sharing pages
const ManageDataSourceSharingPage = lazy(() => import('./pages/data-source/manage-data-source-sharing-page'))
const ManageLayerLinkSharingPage = lazy(() => import('./pages/layer-link/manage-layer-link-sharing-page'))

// Layer links pages
const LayerLinksPage = lazy(() => import('./pages/layer-link/layer-links-page'))
const SelectLayerPage = lazy(() => import('./pages/select-layer-page'))
const NewLayerLinkPage = lazy(() => import('./pages/layer-link/new-layer-link-page'))
const EditLayerLinkPage = lazy(() => import('./pages/layer-link/edit-layer-link-page'))

// Data sources pages
const DataSourcesPage = lazy(() => import('./pages/data-source/data-sources-page'))
const NewDataSourcePage = lazy(() => import('./pages/data-source/new-data-source-page'))
const EditDataSourcePage = lazy(() => import('./pages/data-source/edit-data-source-page'))

// Launcher pages
const LaunchersPage = lazy(() => import('./pages/launcher/launchers-page'))
const NewLauncherPage = lazy(() => import('./pages/launcher/new-launcher-page'))
const EditLauncherPage = lazy(() => import('./pages/launcher/edit-launcher-page'))

// Searches pages
const SearchesPage = lazy(() => import('./pages/search/searches-page'))
const NewSearchPage = lazy(() => import('./pages/search/new-search-page'))
const EditSearchPage = lazy(() => import('./pages/search/edit-search-page'))

// Reporting pages
const ReportsPage = lazy(() => import('./pages/report/reports-page'))
const NewReportPage = lazy(() => import('./pages/report/new-report-page'))
const EditReportPage = lazy(() => import('./pages/report/edit-report-page'))

// Setup pages
const SetupLayout = lazy(() => import('./layouts/setup-layout'))
const SetupPage = lazy(() => import('./pages/setup-page'))
const SetupRegisterAppPage = lazy(() => import('./pages/setup-page/register-app'))
const SetupInstallIRSPage = lazy(() => import('./pages/setup-page/install-irs'))
const SetupInstallWidgetPage = lazy(() => import('./pages/setup-page/install-widget'))
const SetupIRSPage = lazy(() => import('./pages/setup-irs'))
const SettingsPage = lazy(() => import('./pages/settings-page'))

// Hosted Exb Pages
const HostedExbPage = lazy(() => import('./pages/hosted-exb'))

// System console
const SystemLayout = lazy(() => import('./layouts/system-layout'))
const ConsoleHomePage = lazy(() => import('./pages/console/console-home-page'))
const TenantsPage = lazy(() => import('./pages/console/tenant/tenants-page'))
const NewTenantPage = lazy(() => import('./pages/console/tenant/new-tenant-page'))
const EditTenantPage = lazy(() => import('./pages/console/tenant/edit-tenant-page'))
const ConfigTenantPage = lazy(() => import('./pages/console/tenant/config-tenant-page'))
const FeatureConfigPage = lazy(() => import('./pages/console/tenant/feature-config-page'))
const PluginConfigPage = lazy(() => import('./pages/console/tenant/plugin-config-page'))
const RelayServicePage = lazy(() => import('./pages/console/tenant/relay-service-page'))
const PluginsPage = lazy(() => import('./pages/console/plugins-page'))
const NewPluginPage = lazy(() => import('./pages/console/new-plugin-page'))
const EditPluginPage = lazy(() => import('./pages/console/edit-plugin-page'))
const ActivitiesPage = lazy(() => import('./pages/console/tenant/activities-page'))

function createSuspenseElement (page) {
  return (
    <Suspense fallback={<FullPageLoading />}>
      {page}
    </Suspense>
  )
}

export const routes = {
  home: () => '/',
  adminHome: () => '/admin',
  auth: () => '/auth', // intermediate page after authorization success
  setup: () => '/setup',
  setupPortal: (relative = true) => {
    return relative ? 'portal' : '/setup/portal'
  },
  setupRegister: (relative = true) => {
    return relative ? 'register' : '/setup/register'
  },
  setupInstallIRS: (relative = true) => {
    return relative ? 'install-irs' : '/setup/install-irs'
  },
  setupInstallIRSInfo: (relative = true) => {
    return relative ? 'install-irs/info' : '/setup/install-irs/info'
  },
  setupInstallWidget: (relative = true) => {
    return relative ? 'install-widget' : '/setup/install-widget'
  },
  dashboard: () => {
    return '/dashboard'
  },
  hostedExb: () => {
    return '/hosted-exb'
  },
  setupIRS: (relative = true) => {
    return relative ? 'setup-relay-service' : '/setup-relay-service'
  },
  dataSource: (relative = true) => {
    return relative ? 'data-sources' : '/data-sources'
  },
  dataSourceNew: (relative = true) => {
    return relative ? 'new' : '/data-sources/new'
  },
  dataSourceSharing: (id, relative = true) => {
    if (relative) {
      return id ? `${id}/sharing` : ':id/sharing'
    }
    return id ? `/data-sources/${id}/sharing` : '/data-sources/:id/sharing'
  },
  dataSourceEdit: (id, relative = true) => {
    if (relative) {
      return id ? `${id}/edit` : ':id/edit'
    }
    return id ? `/data-sources/${id}/edit` : '/data-sources/:id/edit'
  },
  layerLink: (relative = true) => {
    return relative ? 'layer-links' : '/layer-links'
  },
  layerLinkSelectLayer: (relative = true) => {
    return relative ? 'select-layer' : '/layer-links/select-layer'
  },
  layerLinkNew: (relative = true) => {
    return relative ? 'new' : '/layer-links/new'
  },
  layerLinkSharing: (id, relative = true) => {
    if (relative) {
      return id ? `${id}/sharing` : ':id/sharing'
    }
    return id ? `/layer-links/${id}/sharing` : '/layer-links/:id/sharing'
  },
  layerLinkEdit: (id, relative = true) => {
    if (relative) {
      return id ? `${id}/edit` : ':id/edit'
    }
    return id ? `/layer-links/${id}/edit` : '/layer-links/:id/edit'
  },
  launcher: (relative = true) => {
    return relative ? 'launchers' : '/launchers'
  },
  launcherSelectLayer: (relative = true) => {
    return relative ? 'select-layer' : '/launchers/select-layer'
  },
  launcherNew: (relative = true) => {
    return relative ? 'new' : '/launchers/new'
  },
  launcherEdit: (id, relative = true) => {
    if (relative) {
      return id ? `${id}/edit` : ':id/edit'
    }
    return id ? `/launchers/${id}/edit` : '/launchers/:id/edit'
  },
  launcherError: () => {
    return '/launcher-error'
  },
  searchDefinition: (relative = true) => {
    return relative ? 'search-definitions' : '/search-definitions'
  },
  searchDefinitionSelectLayer: (relative = true) => {
    return relative ? 'select-layer' : '/search-definitions/select-layer'
  },
  searchDefinitionNew: (relative = true) => {
    return relative ? 'new' : '/search-definitions/new'
  },
  searchDefinitionEdit: (id, relative = true) => {
    if (relative) {
      return id ? `${id}/edit` : ':id/edit'
    }
    return id ? `/search-definitions/${id}/edit` : '/search-definitions/:id/edit'
  },
  reportDefinition: (relative = true) => {
    return relative ? 'report-definitions' : '/report-definitions'
  },
  reportDefinitionSelectLayer: (relative = true) => {
    return relative ? 'select-layer' : '/report-definitions/select-layer'
  },
  reportDefinitionNew: (relative = true) => {
    return relative ? 'new' : '/report-definitions/new'
  },
  reportDefinitionEdit: (id, relative = true) => {
    if (relative) {
      return id ? `${id}/edit` : ':id/edit'
    }
    return id ? `/report-definitions/${id}/edit` : '/report-definitions/:id/edit'
  },
  login: () => '/login',
  console: () => {
    return '/console'
  },
  tenants: (relative = true) => {
    if (relative) {
      return 'tenants'
    }
    return '/console/tenants'
  },
  tenantsNew: (relative = true) => {
    if (relative) {
      return 'tenants/new'
    }
    return '/console/tenants/new'
  },
  tenantsEdit: (id, relative = true) => {
    if (relative) {
      return id ? `tenants/${id}/edit` : 'tenants/:id/edit'
    }
    return id ? `/console/tenants/${id}/edit` : '/console/tenants/:id/edit'
  },
  tenantsConfigs: (id, relative = true) => {
    if (relative) {
      return id ? `tenants/${id}/configs` : 'tenants/:id/configs'
    }
    return id ? `/console/tenants/${id}/configs` : '/console/tenants/:id/configs'
  },
  tenantRelayService: (id, relative = true) => {
    if (relative) {
      return id ? `tenants/${id}/relay-service` : 'tenants/:id/relay-service'
    }
    return id ? `/console/tenants/${id}/relay-service` : '/console/tenants/:id/relay-service'
  },
  featureConfigs: (id, relative = true) => {
    if (relative) {
      return id ? `tenants/${id}/features` : 'tenants/:id/features'
    }
    return id ? `/console/tenants/${id}/features` : '/console/tenants/:id/features'
  },
  pluginConfigs: (id, relative = true) => {
    if (relative) {
      return id ? `tenants/${id}/plugins` : 'tenants/:id/plugins'
    }
    return id ? `/console/tenants/${id}/plugins` : '/console/tenants/:id/plugins'
  },
  activities: (id, relative = true) => {
    if (relative) {
      return id ? `tenants/${id}/activities` : 'tenants/:id/activities'
    }
    return id ? `/console/tenants/${id}/activities` : '/console/tenants/:id/activities'
  },
  plugins: (relative = true) => {
    if (relative) {
      return 'plugins'
    }
    return '/console/plugins'
  },
  pluginsNew: (relative = true) => {
    if (relative) {
      return 'plugins/new'
    }
    return '/console/plugins/new'
  },
  pluginsEdit: (id, relative = true) => {
    if (relative) {
      return id ? `plugins/${id}/edit` : 'plugins/:id/edit'
    }
    return id ? `/console/plugins/${id}/edit` : '/console/plugins/:id/edit'
  },
  organization: (relative = true) => {
    return relative ? 'organization' : '/organization'
  },
  organizationSettings: (relative = true) => {
    return relative ? 'settings' : '/organization/settings'
  }
}

const RequireAuth = ({ children, role }) => {
  const { hasToken, currentUser, isLoading } = useAppData()
  const location = useLocation()

  if (!hasToken) {
    // Redirect unauthenticated user to login page
    const searchParams = new URLSearchParams(location.search)
    if (location.pathname !== '/') {
      searchParams.set('next', location.pathname)
    }
    const redirectUrl = routes.login() + '?' + searchParams.toString()
    return <Navigate to={redirectUrl} replace />
  }

  if (isLoading) {
    return (<FullPageLoading />)
  }

  if (currentUser && currentUser.role !== role) {
    if (currentUser.role === 'USER') {
      const errorRoute = routes.auth() + '?error=invalid-role-user'
      return <Navigate to={errorRoute} replace />
    }
    const roleHome = currentUser.role === 'SYSADMIN'
      ? routes.console()
      : routes.dashboard()
    return <Navigate to={roleHome} replace />
  }

  return children
}

export default function Router (context) {
  return (
    <Routes>
      <Route path={routes.login()} element={<PublicLayout />}>
        <Route index element={<LoginPage />} />
      </Route>
      <Route path={routes.auth()} element={<PublicLayout />}>
        <Route index element={<AuthPage />} />
      </Route>
      <Route path={routes.setup()} element={createSuspenseElement(<SetupLayout />)}>
        <Route index element={<Navigate to={routes.setupPortal() + window.location.search} replace />} />
        <Route path={routes.setupPortal()} element={createSuspenseElement(<SetupPage />)} />
        <Route path={routes.setupRegister()} element={createSuspenseElement(<SetupRegisterAppPage />)} />
        <Route path={routes.setupInstallIRS()} element={createSuspenseElement(<SetupInstallIRSPage cancelRoute={routes.setupRegister(false)} />)} />
        <Route path={routes.setupInstallIRSInfo()} element={createSuspenseElement(<SetupInstallIRSPage cancelRoute={routes.dashboard()} />)} />
        <Route path={routes.setupInstallWidget()} element={createSuspenseElement(<SetupInstallWidgetPage />)} />
      </Route>
      <Route
        path={routes.home()} element={
          <RequireAuth role='ADMIN'>
            {createSuspenseElement(<AdminLayout />)}
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to={routes.dashboard()} replace />} />
        <Route path={routes.dashboard()}>
          <Route index element={createSuspenseElement(<DashboardPage />)} />
          <Route path={routes.setupIRS()} element={createSuspenseElement(<SetupIRSPage />)} />
        </Route>
        <Route path={routes.dataSource()}>
          <Route index element={createSuspenseElement(<DataSourcesPage />)} />
          <Route path={routes.dataSourceNew()} element={createSuspenseElement(<NewDataSourcePage />)} />
          <Route path={routes.dataSourceSharing()} element={createSuspenseElement(<ManageDataSourceSharingPage />)} />
          <Route path={routes.dataSourceEdit()} element={createSuspenseElement(<EditDataSourcePage />)} />
        </Route>
        <Route
          path={routes.organizationSettings(false)}
          element={createSuspenseElement(<SettingsPage cancelRoute={routes.dashboard()} />)}
        />
        <Route path={routes.hostedExb()}>
          <Route index element={createSuspenseElement(<HostedExbPage />)} />
        </Route>
        <Route path={routes.layerLink()}>
          <Route index element={createSuspenseElement(<LayerLinksPage />)} />
          <Route
            path={routes.layerLinkSelectLayer()}
            element={
              createSuspenseElement(
                <SelectLayerPage
                  title='Add layer link'
                  cancelRoute={routes.layerLink(false)}
                  nextRoute={routes.layerLinkNew(false)}
                />
              )
            }
          />
          <Route path={routes.layerLinkNew()} element={createSuspenseElement(<NewLayerLinkPage />)} />
          <Route path={routes.layerLinkSharing()} element={createSuspenseElement(<ManageLayerLinkSharingPage />)} />
          <Route path={routes.layerLinkEdit()} element={createSuspenseElement(<EditLayerLinkPage />)} />
        </Route>
        <Route path={routes.launcher()}>
          <Route index element={createSuspenseElement(<LaunchersPage />)} />
          <Route
            path={routes.launcherSelectLayer()}
            element={
              <SelectLayerPage
                title='Select feature layer'
                cancelRoute={routes.launcher(false)}
                nextRoute={routes.launcherNew(false)}
              />
            }
          />
          <Route path={routes.launcherNew()} element={createSuspenseElement(<NewLauncherPage />)} />
          <Route path={routes.launcherEdit()} element={createSuspenseElement(<EditLauncherPage />)} />
        </Route>
        <Route path={routes.searchDefinition()}>
          <Route index element={createSuspenseElement(<SearchesPage />)} />
          <Route
            path={routes.searchDefinitionSelectLayer()}
            element={
              <SelectLayerPage
                title='Add a search'
                cancelRoute={routes.searchDefinition(false)}
                nextRoute={routes.searchDefinitionNew(false)}
              />
            }
          />
          <Route path={routes.searchDefinitionNew()} element={createSuspenseElement(<NewSearchPage />)} />
          <Route path={routes.searchDefinitionEdit()} element={createSuspenseElement(<EditSearchPage />)} />
        </Route>
        <Route path={routes.reportDefinition()}>
          <Route index element={createSuspenseElement(<ReportsPage />)} />
          <Route
            path={routes.reportDefinitionSelectLayer()}
            element={
              <SelectLayerPage
                title='Add report template'
                cancelRoute={routes.reportDefinition(false)}
                nextRoute={routes.reportDefinitionNew(false)}
              />
            }
          />
          <Route path={routes.reportDefinitionNew()} element={createSuspenseElement(<NewReportPage />)} />
          <Route path={routes.reportDefinitionEdit()} element={createSuspenseElement(<EditReportPage />)} />
        </Route>
      </Route>
      <Route
        path={routes.console()}
        element={
          <RequireAuth role='SYSADMIN'>
            {createSuspenseElement(<SystemLayout />)}
          </RequireAuth>
        }
      >
        <Route
          index
          element={createSuspenseElement(<ConsoleHomePage />)}
        />
        <Route
          path={routes.tenants()}
          element={createSuspenseElement(<TenantsPage />)}
        />
        <Route
          path={routes.tenantsNew()}
          element={createSuspenseElement(<NewTenantPage />)}
        />
        <Route
          path={routes.tenantsEdit()}
          element={createSuspenseElement(<EditTenantPage />)}
        />
        <Route
          path={routes.tenantsConfigs()}
          element={createSuspenseElement(<ConfigTenantPage />)}
        />
        <Route
          path={routes.tenantRelayService()}
          element={createSuspenseElement(<RelayServicePage />)}
        />
        <Route
          path={routes.featureConfigs()}
          element={createSuspenseElement(<FeatureConfigPage />)}
        />
        <Route
          path={routes.pluginConfigs()}
          element={createSuspenseElement(<PluginConfigPage />)}
        />
        <Route
          path={routes.pluginsNew()}
          element={createSuspenseElement(<NewPluginPage />)}
        />
        <Route
          path={routes.plugins()}
          element={createSuspenseElement(<PluginsPage />)}
        />
        <Route
          path={routes.pluginsEdit()}
          element={createSuspenseElement(<EditPluginPage />)}
        />
        <Route
          path={routes.activities()}
          element={createSuspenseElement(<ActivitiesPage />)}
        />
      </Route>
      <Route element={<BlankLayout />}>
        <Route path={routes.launcherError()} index element={<ErrorPage />} />
      </Route>
    </Routes>
  )
}
